/**
 * IndexedDB Session Service (ported from Noteble's capture flow — ADR-022).
 * Persists in-progress capture elements locally to survive page refreshes.
 *
 * Storage strategy:
 * - Photos and notes captured on /add are saved under the standalone slot
 *   as they are added, and cleared on submit or discard.
 * - Auto-deletes entries older than 24 hours (drafts are the durable store —
 *   see indexedDBDraftsService).
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('indexedDBSessionService');

const DB_NAME = 'systemdox-recordings';
const DB_VERSION = 1;
const STORE_NAME = 'interrupted-recordings';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Reserved session ID for photos/notes captured outside of a recording session. */
export const STANDALONE_SESSION_ID = '__standalone__';

export interface CapturedPhoto {
  id: string;                    // uuid for uniqueness
  blob: Blob;                    // JPEG/PNG/WebP blob (camera canvas or downscaled upload)
  timestamp: number;             // seconds into recording (0 when standalone)
  capturedAt: number;            // Date.now() for sorting
  description?: string;          // vision-extracted or user-edited description
  /** Tenant media id once uploaded via the media presigner. */
  mediaId?: string;
  /** Mermaid rendition from vision analysis, when the photo is diagram-like. */
  mermaid?: string | null;
}

export interface RecordingNote {
  id: string;                    // uuid for uniqueness
  text: string;                  // note content
  timestamp: number;             // seconds into recording (0 when standalone)
  createdAt: number;             // Date.now() for sorting
  sourceType?: 'note' | 'document';  // origin type — 'document' shows FileText icon
  sourceTitle?: string;          // original document title (when sourceType is 'document')
}

export interface InterruptedRecording {
  sessionId: string;
  chunks: Blob[];
  mimeType: string;
  duration: number;
  languageCode: string;
  startedAt: number;
  lastSavedAt: number;
  photos?: CapturedPhoto[];
  notes?: RecordingNote[];
}

class IndexedDBSessionService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isClosing = false;

  private isDatabaseAvailable(): boolean {
    return this.db !== null && !this.isClosing;
  }

  private async init(): Promise<void> {
    if (this.db && !this.isClosing) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        const error = request.error || new Error('Failed to open IndexedDB');
        log.error('Failed to open IndexedDB', error as Error);
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isClosing = false;

        this.db.onclose = () => {
          this.isClosing = true;
          log.info('IndexedDB connection closed');
        };

        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
          store.createIndex('startedAt', 'startedAt', { unique: false });
          log.info('Created object store', { storeName: STORE_NAME });
        }
      };
    });

    return this.initPromise;
  }

  async saveRecording(recording: InterruptedRecording): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) {
        log.warn('Database not available for save operation');
        return;
      }

      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(recording);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to save recording', err as Error);
      throw err;
    }
  }

  async getRecording(sessionId: string): Promise<InterruptedRecording | null> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return null;

      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.get(sessionId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to get recording', err as Error);
      return null;
    }
  }

  async getAllRecordings(): Promise<InterruptedRecording[]> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return [];

      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const recordings = await new Promise<InterruptedRecording[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      // Filter out stale recordings (older than 24 hours) and the standalone slot
      const now = Date.now();
      const validRecordings = recordings.filter(
        (r) => r.sessionId !== STANDALONE_SESSION_ID && now - r.startedAt < MAX_AGE_MS,
      );

      const staleRecordings = recordings.filter((r) => now - r.startedAt >= MAX_AGE_MS);
      if (staleRecordings.length > 0) {
        log.info('Found stale recordings, cleaning up', { count: staleRecordings.length });
        void Promise.all(staleRecordings.map((r) => this.deleteRecording(r.sessionId)));
      }

      return validRecordings;
    } catch (err) {
      log.error('Failed to get recordings', err as Error);
      return [];
    }
  }

  async deleteRecording(sessionId: string): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return;

      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(sessionId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to delete recording', err as Error);
      throw err;
    }
  }

  async clearAll(): Promise<void> {
    try {
      await this.init();
      if (!this.isDatabaseAvailable()) return;

      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to clear recordings', err as Error);
      throw err;
    }
  }

  async checkStorageQuota(): Promise<{ available: boolean; usage?: number; quota?: number }> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const available = quota > 0 && usage / quota < 0.9; // Less than 90% used
        return { available, usage, quota };
      }
      return { available: true };
    } catch {
      return { available: true };
    }
  }

  close(): void {
    if (this.db) {
      this.isClosing = true;
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }
}

// Export singleton instance
export const indexedDBSessionService = new IndexedDBSessionService();
